import React, {useCallback, useMemo, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import Api from "../../../../core/services/api/api";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import Button from "../../base/button/button";
import TagAutocomplete from "../../../components/text-fields/autocomplete/tag-autocomplete";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../base/chips/chips";
import TagChipsContent from "../../app-specific/chips-contents/tag-chips-content";
import ToastService from "../../../../core/services/toast";
import useIndustry from "../../../hooks/use-industry";
import {InputFileAcceptMimes, InputFileAcceptTypes} from "../../../../core/constants/enums";
import DropzoneField from "../../text-fields/dropzone-field";
import FileUtils from "../../../../core/services/file-utils";
import Form, {MuiRFF, Yup} from "../../form";
import {Col} from "reactstrap";
import {useBizFileUrlsResolver} from "../../../hooks/use-biz-file-urls-resolver";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import UserHomepageUtils from "../../../containers/user-homepage/core/utils";

const formNames = {
    file: 'file',
    imageWidth: 'imageWidth',
    imageHeight: 'imageHeight',
    tags: 'tags',
};

const FORM_ID = 'carousel-image-form';

const optionalPositiveDimension = Yup.string().nullable().test(
    'positive-dimension',
    'Must be a positive number',
    (value) => {
        if (value === '' || value == null) {
            return true;
        }
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0;
    },
);

const buildSchema = (isEdit) => Yup.object().shape({
    [formNames.file]: isEdit
        ? Yup.string().nullable()
        : Yup.string().nullable().required(ValidateMessages.required),
    [formNames.imageWidth]: optionalPositiveDimension,
    [formNames.imageHeight]: optionalPositiveDimension,
    [formNames.tags]: Yup.array().of(Yup.mixed()).test(
        'same-industry',
        'All tags must belong to the same industry.',
        (tags) => {
            if (!tags?.length) {
                return true;
            }
            const lockedIndustryId = tags.find((tag) => tag?.industryId)?.industryId;
            if (!lockedIndustryId) {
                return true;
            }
            return !tags.some((tag) => tag?.industryId && tag.industryId !== lockedIndustryId);
        },
    ),
});

/**
 * Parses a file reference string into its GUID and extension components.
 * @param value
 * @returns {{fileGuid: string|undefined, fileExtension: string|undefined}}
 */
const parseFileReference = (value) => {
    if (!value || typeof value !== 'string') {
        return {fileGuid: undefined, fileExtension: undefined};
    }
    const parts = value.split('.');
    if (parts.length < 2) {
        return {fileGuid: value, fileExtension: undefined};
    }
    const fileExtension = parts.pop();
    return {fileGuid: parts.join('.'), fileExtension};
};

/**
 * Converts file GUID and extension into a single string reference for form handling.
 * @param fileGuid
 * @param fileExtension
 * @returns {string|undefined}
 */
const toFileReference = (fileGuid, fileExtension) => {
    if (!fileGuid) {
        return undefined;
    }
    return fileExtension ? `${fileGuid}.${fileExtension}` : fileGuid;
};

/**
 * Parses a dimension input value, ensuring it's a positive number or null if empty.
 * @param value
 * @returns {number|null}
 */
const parseDimension = (value) => {
    if (value === '' || value == null) {
        return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const CarouselImageDialog = ({
                                 open,
                                 close,
                                 mode = 'create',
                                 image = null,
                                 existingImages = [],
                                 imageWidth: initialImageWidth = null,
                                 imageHeight: initialImageHeight = null,
                                 onSuccess,
                             }) => {
    const isEdit = mode === 'edit';
    const [submitting, setSubmitting] = useState(false);
    const isMounted = useIsMounted();
    const {industries} = useIndustry({noCategories: true});
    const bizFileUrls = useBizFileUrlsResolver((t) => t.bizFile);

    const schema = useMemo(() => buildSchema(isEdit), [isEdit]);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        [formNames.file]: isEdit ? toFileReference(image?.fileGuid, image?.fileExtension) : undefined,
        [formNames.imageWidth]: initialImageWidth != null ? String(initialImageWidth) : '',
        [formNames.imageHeight]: initialImageHeight != null ? String(initialImageHeight) : '',
        [formNames.tags]: isEdit ? (image?.tags ?? []) : [],
    }), [isEdit, image, initialImageWidth, initialImageHeight]);

    const formKey = `${mode}-${image?.id ?? 'new'}`;

    const addTag = (form, tag) => {
        if (!tag?.id) {
            return;
        }
        const currentTags = form.getState().values[formNames.tags] ?? [];
        if (currentTags.some((t) => t.id === tag.id)) {
            return;
        }
        const lockedIndustryId = currentTags.find((t) => t.industryId)?.industryId;
        if (lockedIndustryId && tag.industryId && tag.industryId !== lockedIndustryId) {
            ToastService.error({
                title: 'Invalid tag',
                message: 'This tag belongs to a different industry.',
            });
            return;
        }
        form.change(formNames.tags, [...currentTags, tag]);
    };

    const removeTag = (form, tag) => {
        const currentTags = form.getState().values[formNames.tags] ?? [];
        form.change(formNames.tags, currentTags.filter((t) => t.id !== tag.id));
    };

    const onSubmit = async (values) => {
        setSubmitting(true);
        try {
            const {fileGuid, fileExtension} = parseFileReference(values?.[formNames.file]);
            const tags = values?.[formNames.tags] ?? [];
            const parsedWidth = parseDimension(values?.[formNames.imageWidth]);
            const parsedHeight = parseDimension(values?.[formNames.imageHeight]);

            const nextImage = UserHomepageUtils.prepareCarouselImageContent(
                isEdit
                    ? {
                        ...image,
                        fileGuid: fileGuid ?? image?.fileGuid,
                        fileExtension: fileExtension ?? image?.fileExtension,
                        tags,
                    }
                    : {
                        fileGuid,
                        fileExtension,
                        tags,
                        id: UserHomepageUtils.getTempCarouselImageId(existingImages),
                        orderIndex: existingImages.length + 1,
                    },
                isEdit ? image?.orderIndex : existingImages.length + 1,
            );

            if (!isMounted()) {
                return;
            }

            onSuccess({
                image: nextImage,
                imageWidth: parsedWidth,
                imageHeight: parsedHeight,
            });
            close();
        } finally {
            if (isMounted()) {
                setSubmitting(false);
            }
        }
    };

    const getFullFileUrl = useCallback((fileReference) => {
        const {fileGuid, fileExtension} = parseFileReference(fileReference);
        return bizFileUrls.getFilePreviewUrl(fileGuid, fileExtension);
    }, [bizFileUrls]);

    return (
        <Dialog
            open={open}
            setOpen={() => close()}
            maxWidth={'sm'}
            className={'bound-dialog'}
            loading={submitting}
            disableEscapeKeyDown={submitting}
            disableBackdropClick={submitting}
            scroll={'paper'}
            aria-labelledby="carousel-image-dialog-title"
        >
            <DialogTitle id={'carousel-image-dialog-title'}>
                <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                    {isEdit ? 'Edit Carousel Image' : 'Add Carousel Image'}
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent className={'px-4 mb-4 mt-3'}>
                <Form
                    key={formKey}
                    formProps={{
                        id: FORM_ID,
                        className: 'd-flex flex-wrap w-100',
                    }}
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialValues}
                >
                    {({form, values}) => {
                        const tags = values?.[formNames.tags] ?? [];
                        const industryIdFromTags = tags.find((e) => !!e.industryId)?.industryId ?? null;
                        const industry = industries.find((i) => i.id === industryIdFromTags);
                        return (
                            <>
                                <Col xs={12} className={'mb-3 px-0'}>
                                    <DropzoneField
                                        disableAutoFormChange
                                        name={formNames.file}
                                        accept={[
                                            InputFileAcceptMimes.images,
                                            InputFileAcceptTypes.heic,
                                            InputFileAcceptTypes.heif,
                                        ]}
                                        disabled={submitting}
                                        required={!isEdit && !!required[formNames.file]}
                                        getFullFileUrl={getFullFileUrl}
                                        getFileGuid={(data) => data.id}
                                        getFileExtension={(data) => data.extension}
                                        initialFileUpload={(args) =>
                                            Api.uploadFile({
                                                file: args.chunk,
                                                fileName: args.fileName,
                                                fileSize: args.fileSize,
                                            }, args.onUploadProgress)
                                        }
                                        uploadFile={(...args) => FileUtils.uploadFile(...args)}
                                        onChange={(value) => form.change(formNames.file, value)}
                                    />
                                </Col>
                                <Col xs={6} className={'mb-3 px-0 pr-2'}>
                                    <MuiRFF.TextField
                                        fullWidth
                                        type={'number'}
                                        name={formNames.imageWidth}
                                        label={'Image width (px)'}
                                        placeholder={'optional'}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={6} className={'mb-3 px-0 pl-2'}>
                                    <MuiRFF.TextField
                                        fullWidth
                                        type={'number'}
                                        name={formNames.imageHeight}
                                        label={'Image height (px)'}
                                        placeholder={'optional'}
                                        disabled={submitting}
                                    />
                                </Col>
                                <Col xs={12} className={'px-0'}>
                                    <p className={'text-sm text-color-2 mb-2'}>Tags (optional — enables search on click)</p>
                                    <div className={'d-flex flex-wrap mb-2'}>
                                        {tags.map((tag) => (
                                            <Chips
                                                key={tag.id}
                                                type={ChipsTypes.remove}
                                                content={<TagChipsContent tag={tag} useIcon/>}
                                                onIconClick={() => removeTag(form, tag)}
                                                className={'mr-2 mb-2'}
                                                contentType={ChipsContentType.element}
                                                iconPosition={ChipsIconPosition.start}
                                                disabled={submitting}
                                                buttonProps={{size: 'small'}}
                                            />
                                        ))}
                                    </div>
                                    <TagAutocomplete
                                        value={null}
                                        clearOnBlur
                                        selectedTags={tags}
                                        disabled={submitting}
                                        onChange={(e, t) => addTag(form, t)}
                                        industryId={industry?.id}
                                        label={'Add tag'}
                                    />
                                </Col>
                            </>
                        );
                    }}
                </Form>
            </DialogContent>
            <Divider className='divider'/>
            <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                <Button
                    variant={'dialog'}
                    color={'primary'}
                    type={'button'}
                    disabled={submitting}
                    onClick={() => close()}
                    borderRadius={'0 8px 0 8px'}
                >
                    Close
                </Button>
                <Button
                    onClick={() => document.getElementById(FORM_ID)?.dispatchEvent(new Event('submit', {cancelable: true, bubbles: true}))}
                    disabled={submitting}
                    variant={'dialog'}
                    color={'primary'}
                    type={'submit'}
                    form={FORM_ID}
                    borderRadius={'8px 0 8px 0'}
                >
                    {submitting ? 'Submitting...' : isEdit ? 'Save Changes' : 'Add Image'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CarouselImageDialog;
